import React, { useState, useEffect, useCallback } from 'react';
import { message, Select } from 'antd';
import { ClipLoader } from 'react-spinners';
import { saveAs } from 'file-saver';
import { apiGetGroups, apiGetEmployeesByGroup, apiExportSalaryExcel, apiCloseSalaryMonth } from '../../api';

const { Option } = Select;

const ExportSalaryExcel = () => {
    const [groups, setGroups] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isExporting, setIsExporting] = useState(false);
    const [isClosing, setIsClosing] = useState(false); 
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const today = new Date();
    const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const defaultYymm = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

    const [selectedMonth, setSelectedMonth] = useState(defaultYymm);
    const [selectedGroup, setSelectedGroup] = useState('ALL');
    const [selectedEmp, setSelectedEmp] = useState('ALL');

    useEffect(() => {
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const groupsData = await apiGetGroups();
                setGroups(groupsData);
            } catch (error) {
                message.error('Không thể tải danh sách bộ phận.');
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchGroups();
    }, []);

    const handleGroupChange = useCallback(async (groupid) => {
        setSelectedGroup(groupid);
        setSelectedEmp('ALL');
        
        if (!selectedMonth) {
            setEmployees([]);
            return;
        }

        const yymm = selectedMonth.replace('-', '');
        setLoadingEmployees(true);
        try {
            const data = await apiGetEmployeesByGroup(groupid, yymm);
            setEmployees(data);
        } catch (error) {
            message.error('Không thể tải danh sách nhân viên.');
        } finally {
            setLoadingEmployees(false);
        }
    }, [selectedMonth]);

    useEffect(() => {
        handleGroupChange(selectedGroup);
    }, [selectedMonth, selectedGroup, handleGroupChange]);
    
    // CẬP NHẬT: Thêm biến limit16h
    const handleExport = async (limit16h = false) => {
        setIsExporting(true);
        try {
            const filters = {
                yymm: selectedMonth,
                groupid: selectedGroup,
                empid: selectedEmp,
                limit16h: limit16h // Gửi sang API
            };

            const blob = await apiExportSalaryExcel(filters);

            saveAs(blob, `BangLuong_${limit16h?'16h_':''}${selectedMonth.replace('-', '_')}.xlsx`);
            message.success(`Xuất file Excel ${limit16h ? '16h' : 'gốc'} thành công!`);

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Có lỗi xảy ra khi xuất file.';
            message.error(errorMessage);
        } finally {
            setIsExporting(false);
        }
    };

    const handleCloseMIS = async () => {
        if (!selectedMonth) { message.warn('Vui lòng chọn tháng để chốt sổ.'); return; }
        const confirmation = window.confirm(`Bạn có chắc chắn muốn CHỐT SỔ LƯƠNG cho tháng ${selectedMonth}?\n\nThao tác này sẽ sao lưu dữ liệu lương sang bảng backup và không thể hoàn tác.`);
        if (confirmation) {
            setIsClosing(true);
            try {
                const result = await apiCloseSalaryMonth({ yymm: selectedMonth });
                message.success(result.message || 'Chốt sổ thành công!');
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Có lỗi xảy ra khi chốt sổ.';
                message.error(errorMessage);
            } finally {
                setIsClosing(false);
            }
        }
    };

    return (
        <div style={{ fontFamily: '"Times New Roman", Times, serif' }} className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Xuất Báo Cáo Lương Excel & Chốt Sổ</h2>
            
            <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6 max-w-4xl">
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Tháng</label>
                    <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="mt-1 p-2 border rounded-md" style={{ fontFamily: 'inherit' }} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Bộ phận</label>
                    <Select value={selectedGroup} onChange={handleGroupChange} loading={loadingGroups} style={{ width: 240 }} className="mt-1">
                        <Option value="ALL">-- Tất cả --</Option>
                        {groups.map(g => <Option key={g.groupId} value={g.groupId}>{g.groupName} ({g.groupId})</Option>)}
                    </Select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Nhân viên</label>
                    <Select
                        showSearch
                        value={selectedEmp}
                        onChange={setSelectedEmp}
                        loading={loadingEmployees}
                        disabled={loadingEmployees || !employees.length}
                        style={{ width: 240 }}
                        placeholder="Tìm và chọn nhân viên"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            const optionText = (Array.isArray(option.children) ? option.children.join('') : option.children) || '';
                            return optionText.toLowerCase().includes(input.toLowerCase());
                        }}
                        className="mt-1"
                    >
                        <Option value="ALL">-- Tất cả --</Option>
                        {employees.map(e => <Option key={e.empid} value={e.empid}>{e.name} ({e.empid})</Option>)}
                    </Select>
                </div>
                 <div className="flex items-center gap-2">
                    {/* CẬP NHẬT: Chia làm 2 nút Xuất Gốc và Xuất 16h */}
                    <button onClick={() => handleExport(false)} disabled={isExporting || isClosing} className="px-6 py-2 font-semibold text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-gray-400">
                        {isExporting ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Đang xử lý</> : 'Xuất Excel (Gốc)'}
                    </button>

                    <button onClick={() => handleExport(true)} disabled={isExporting || isClosing} className="px-6 py-2 font-semibold text-white bg-indigo-500 rounded-md hover:bg-indigo-600 disabled:bg-gray-400">
                        {isExporting ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Đang xử lý</> : 'Xuất Excel (16h)'}
                    </button>

                    <button onClick={handleCloseMIS} disabled={isClosing || isExporting} className="px-6 py-2 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-400 ml-4">
                        {isClosing ? <><ClipLoader size={20} color={"#ffffff"} className="mr-2" /> Đang chốt...</> : 'Chốt Sổ MIS'}
                    </button>
                </div>
            </div>

            <div className="mt-6 p-4 border-l-4 border-yellow-500 bg-yellow-50 max-w-4xl">
                <p className="text-sm text-yellow-800">
                    <span className="font-bold">Lưu ý:</span> Chức năng <span className="font-semibold">'Chốt Sổ MIS'</span> sẽ sao lưu dữ liệu lương của tháng đã chọn vào bảng chính thức. Hãy đảm bảo mọi dữ liệu đã chính xác trước khi thực hiện.
                </p>
            </div>
        </div>
    );
};

export default ExportSalaryExcel;